import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  AccountBalanceWalletOutlined,
  AttachMoneyOutlined,
  CalculateOutlined,
  CompareArrowsOutlined,
  InfoOutlined,
  Inventory2Outlined,
  LocalGasStationOutlined,
  LocationOnOutlined,
  PercentOutlined,
  PeopleOutlineOutlined,
  PointOfSaleOutlined,
  ReportProblemOutlined,
  ShoppingCartOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid2';

const BRAND = {
  pageBackground: '#eef4fb',
  cardBackground: '#ffffff',
  border: '#d6e2f1',
  textPrimary: '#0f2342',
  textSecondary: '#5f7591',
  gold: '#60a5fa',
  amber: '#3b82f6',
  rust: '#1d4ed8',
  cobalt: '#2563eb',
  teal: '#38bdf8',
  success: '#2563eb',
  danger: '#64748b',
};

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
});

const decimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBwp = (value) => `BWP ${decimalFormatter.format(toNumber(value))}`;
const formatCompactBwp = (value) => `BWP ${compactFormatter.format(toNumber(value))}`;
const formatLitres = (value) => `${integerFormatter.format(toNumber(value))} L`;
const formatDecimalLitres = (value) => `${decimalFormatter.format(toNumber(value))} L`;
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const formatValueOrDash = (value, formatter) => (hasValue(value) ? formatter(value) : '-');
const formatPercentOrDash = (value) => (hasValue(value) ? `${toNumber(value).toFixed(2)}%` : '-');

const formatDailyStatisticValue = (value, format) => {
  if (format === 'bwp') {
    return formatBwp(value);
  }
  if (format === 'litres') {
    return formatLitres(value);
  }
  if (format === 'decimal_litres') {
    return formatDecimalLitres(value);
  }
  if (format === 'integer') {
    return integerFormatter.format(toNumber(value));
  }
  return decimalFormatter.format(toNumber(value));
};

const formatDailyStatisticAxis = (value, format) => {
  const numericValue = toNumber(value);
  if (format === 'bwp') {
    return `BWP ${compactFormatter.format(numericValue)}`;
  }
  if (format === 'litres' || format === 'decimal_litres') {
    return `${compactFormatter.format(numericValue)} L`;
  }
  return compactFormatter.format(numericValue);
};

const getReportDate = (startDate, endDate) => {
  const preferredDate = endDate || startDate;
  const parsed = preferredDate ? new Date(preferredDate) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const formatTenderName = (value) => {
  if (!value) {
    return 'Unknown';
  }
  return String(value)
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const DASHBOARD_SECTIONS = [
  { key: 'sales', label: 'Fuel Sales' },
  { key: 'trend', label: 'Daily Statistics' },
  { key: 'reconciliation', label: 'Reconciliation' },
  { key: 'cashier', label: 'Cashier' },
];

const DAILY_STATISTIC_METRICS = [
  { key: 'total_fuel_volume', label: 'Total Fuel Volume', format: 'litres', color: BRAND.amber },
  { key: 'number_of_fuel_customers', label: 'Fuel Customers', format: 'integer', color: BRAND.cobalt },
  { key: 'number_of_line_items', label: 'Line Items', format: 'integer', color: BRAND.teal },
  { key: 'total_fuel_sales_value', label: 'Total Fuel Sales Value', format: 'bwp', color: BRAND.rust },
  { key: 'average_fuel_sales_value', label: 'Average Fuel Sales Value', format: 'bwp', color: '#1e40af' },
  { key: 'average_fill', label: 'Average Fill', format: 'decimal_litres', color: '#0369a1' },
];

const FUEL_SALES_CONTROL_METRICS = [
  { key: 'days_pump_vol_sales', label: 'Pump Volume Sales', format: 'decimal_litres' },
  { key: 'days_amount_sales', label: 'Sales Amount', format: 'bwp' },
  { key: 'days_tank_vol_sales', label: 'Tank Volume Sales', format: 'decimal_litres' },
  { key: 'days_pump_vol_variance', label: 'Pump Volume Variance', format: 'decimal_litres' },
  { key: 'opening_vol', label: 'Opening Volume', format: 'decimal_litres' },
  { key: 'vol_purchase', label: 'Volume Purchased', format: 'decimal_litres' },
  { key: 'closing_dip', label: 'Closing Dip', format: 'decimal_litres' },
];

const PUMP_VARIANCE_METRICS = [
  { key: 'inc_unsettled_vol_variance', label: 'Variance incl. Unsettled', format: 'decimal_litres' },
  { key: 'volume_variance', label: 'Volume Variance', format: 'decimal_litres' },
  { key: 'actual_sales_volume', label: 'Actual Sales Volume', format: 'decimal_litres' },
  { key: 'calculated_sales_volume', label: 'Calculated Sales Volume', format: 'decimal_litres' },
];

const FUEL_SHARE_METRICS = [
  { key: 'volume_share_pct', label: 'Volume Share %', totalKey: 'total_volume', totalFormat: 'decimal_litres' },
  { key: 'sales_share_pct', label: 'Sales Share %', totalKey: 'total_sales', totalFormat: 'bwp' },
  { key: 'variance_share_pct', label: 'Variance Share %', totalKey: 'total_variance', totalFormat: 'decimal_litres' },
];

const SHOW_RECONCILIATION_SNAPSHOT_TABLES = false;

const batchFilterOptions = createFilterOptions({
  stringify: (option) =>
    `${option?.batch_number ?? ''} ${option?.id ?? ''} ${option?.depot_id ?? ''}`.trim(),
  limit: 120,
  trim: true,
});

const SectionHeading = ({ index, title, description }) => (
  <Box sx={{ mb: 2 }}>
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Typography sx={{ color: '#7a93b3', letterSpacing: 1.2, fontSize: 15 }}>{index}</Typography>
      <Typography
        variant="h4"
        sx={{
          color: BRAND.textPrimary,
          fontWeight: 700,
          lineHeight: 1.1,
          fontFamily: '"Playfair Display", "Georgia", serif',
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Typography sx={{ color: BRAND.textSecondary, mt: 1, maxWidth: 880 }}>{description}</Typography>
    <Divider sx={{ borderColor: BRAND.border, mt: 2 }} />
  </Box>
);

SectionHeading.propTypes = {
  index: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const MetricTile = ({ icon: Icon, label, value, hint, accent }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${BRAND.border}`,
      borderRadius: 2.5,
      p: 2.25,
      height: '100%',
      background: BRAND.cardBackground,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 5,
        backgroundColor: accent,
      }}
    />
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Box>
        <Typography
          variant="caption"
          sx={{ textTransform: 'uppercase', color: BRAND.textSecondary, letterSpacing: 1.1 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: BRAND.textPrimary,
            fontSize: { xs: '1.8rem', md: '2rem' },
            fontWeight: 700,
            mt: 0.8,
            lineHeight: 1.1,
            fontFamily: '"Playfair Display", "Georgia", serif',
          }}
        >
          {value}
        </Typography>
        <Typography sx={{ color: BRAND.textSecondary, mt: 0.8 }}>{hint}</Typography>
      </Box>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 1.6,
          backgroundColor: `${accent}22`,
          color: accent,
          display: 'grid',
          placeItems: 'center',
          mt: 0.2,
        }}
      >
        <Icon />
      </Box>
    </Stack>
  </Paper>
);

MetricTile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

const InfoTile = ({ icon: Icon, label, value, hint, accent }) => (
  <Paper
    elevation={0}
    sx={{
      border: `1px solid ${BRAND.border}`,
      borderRadius: 2.5,
      p: 2.25,
      height: '100%',
      background: BRAND.cardBackground,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          backgroundColor: `${accent}22`,
          color: accent,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{
        color: BRAND.textPrimary,
        fontSize: '2rem',
        fontWeight: 700,
        mt: 1.75,
        fontFamily: '"Playfair Display", "Georgia", serif',
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ color: BRAND.textSecondary }}>{hint}</Typography>
  </Paper>
);

InfoTile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
};

export function DepotExecutiveView({
  depots,
  selectedDepotId,
  onDepotChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClearDates,
  cashVarianceSummary,
  cashVarianceTrend,
  pumpVarianceTrend,
  profitabilitySummary,
  profitabilityComparison,
  fuelSalesControlRevenueByFuelType,
  fuelSalesControlTrend,
  fuelSalesControlVolumeShare,
  dailyStatisticsSummary,
  dailyStatisticsTrend,
  cashierExceptionSummary,
  cashierExceptionTrend,
  depotBatches,
  selectedBatchId,
  onBatchChange,
  cashVarianceBatchBreakdown,
}) {
  const [cashVarianceMetric, setCashVarianceMetric] = useState('variance');
  const [activeSection, setActiveSection] = useState('sales');
  const [dailyStatisticMetric, setDailyStatisticMetric] = useState('total_fuel_volume');
  const [fuelSalesControlMetric, setFuelSalesControlMetric] = useState('days_pump_vol_sales');
  const [pumpVarianceMetric, setPumpVarianceMetric] = useState('inc_unsettled_vol_variance');
  const [fuelShareMetric, setFuelShareMetric] = useState('volume_share_pct');

  useEffect(() => {
    if (!DASHBOARD_SECTIONS.some((section) => section.key === activeSection)) {
      setActiveSection('sales');
    }
  }, [activeSection]);

  const selectedDepot = useMemo(
    () =>
      depots.find((depot) => String(depot.depot_id) === String(selectedDepotId)) ||
      depots[0] ||
      null,
    [depots, selectedDepotId],
  );

  const reportPeriod = useMemo(() => {
    const reportDate = getReportDate(startDate, endDate);
    return reportDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [startDate, endDate]);

  const revenueByFuelTypeRows = useMemo(() => {
    const rows = Array.isArray(fuelSalesControlRevenueByFuelType?.rows)
      ? fuelSalesControlRevenueByFuelType.rows
      : [];
    return rows;
  }, [fuelSalesControlRevenueByFuelType]);

  const profitabilityRows = useMemo(() => {
    const rows = Array.isArray(profitabilityComparison?.rows) ? profitabilityComparison.rows : [];
    return rows.map((row, index) => ({
      key: `profitability-${index}-${row.product || 'product'}`,
      label: row.product || 'Product',
      revenue: toNumber(row.revenue),
      cost: toNumber(row.cost),
      profit: toNumber(row.profit),
      margin: toNumber(row.margin_percentage),
      progress: toNumber(row.progress_percentage),
      accent: index % 2 === 0 ? BRAND.amber : BRAND.rust,
    }));
  }, [profitabilityComparison]);

  const profitabilityTopSummary = useMemo(() => {
    const summary = profitabilitySummary;
    if (!summary || typeof summary !== 'object') {
      return null;
    }

    const hasSummaryValue =
      hasValue(summary.total_revenue) ||
      hasValue(summary.total_volume) ||
      hasValue(summary.gross_profit) ||
      hasValue(summary.gross_margin);

    if (!hasSummaryValue) {
      return null;
    }

    const revenue = hasValue(summary.total_revenue) ? toNumber(summary.total_revenue) : null;
    const volume = hasValue(summary.total_volume) ? toNumber(summary.total_volume) : null;
    const profit = hasValue(summary.gross_profit) ? toNumber(summary.gross_profit) : null;
    const margin = hasValue(summary.gross_margin)
      ? toNumber(summary.gross_margin)
      : hasValue(revenue) && hasValue(profit) && toNumber(revenue) > 0
      ? (toNumber(profit) / toNumber(revenue)) * 100
      : null;
    const cost = hasValue(revenue) && hasValue(profit) ? Math.max(toNumber(revenue) - toNumber(profit), 0) : null;

    return {
      revenue,
      volume,
      profit,
      margin,
      cost,
    };
  }, [profitabilitySummary]);

  const cashVarianceRows = useMemo(() => {
    const trendSeries = Array.isArray(cashVarianceTrend?.series) ? cashVarianceTrend.series : [];
    if (!trendSeries.length) {
      return [];
    }

    const rows = trendSeries.map((series) => {
      const points = Array.isArray(series?.data) ? series.data : [];
      const actual = points.reduce((sum, point) => sum + toNumber(point?.actual), 0);
      const theoretical = points.reduce((sum, point) => sum + toNumber(point?.theoretical), 0);
      return {
        tender: formatTenderName(series?.name),
        actual,
        theoretical,
      };
    });

    if (!rows.length) {
      return [];
    }

    const total = rows.reduce(
      (acc, row) => ({
        actual: acc.actual + row.actual,
        theoretical: acc.theoretical + row.theoretical,
      }),
      { actual: 0, theoretical: 0 },
    );

    return [...rows, { tender: 'Total', actual: total.actual, theoretical: total.theoretical }];
  }, [cashVarianceTrend]);

  const fuelControlRows = useMemo(() => {
    const rows = Array.isArray(fuelSalesControlTrend?.rows) ? fuelSalesControlTrend.rows : [];
    if (!rows.length) {
      return [];
    }

    const grouped = rows.reduce((acc, row) => {
      const grade = formatTenderName(
        row?.grade_description || row?.grade_name || row?.grade_id || row?.fuel_type || 'Unknown',
      );

      if (!acc[grade]) {
        acc[grade] = {
          grade,
          pumpVolume: 0,
          pumpSales: 0,
          tankVolume: 0,
        };
      }

      acc[grade].pumpVolume += toNumber(row?.days_pump_vol_sales);
      acc[grade].pumpSales += toNumber(row?.days_amount_sales);
      acc[grade].tankVolume += toNumber(row?.days_tank_vol_sales);
      return acc;
    }, {});

    const normalizedRows = Object.values(grouped);
    if (!normalizedRows.length) {
      return [];
    }

    const totalsRow = normalizedRows.reduce(
      (acc, row) => ({
        grade: 'Total',
        pumpVolume: acc.pumpVolume + row.pumpVolume,
        pumpSales: acc.pumpSales + row.pumpSales,
        tankVolume: acc.tankVolume + row.tankVolume,
      }),
      {
        grade: 'Total',
        pumpVolume: 0,
        pumpSales: 0,
        tankVolume: 0,
      },
    );

    return [...normalizedRows, totalsRow];
  }, [fuelSalesControlTrend]);

  const cashierRows = useMemo(() => {
    const rows = Array.isArray(cashierExceptionTrend?.rows) ? cashierExceptionTrend.rows : [];
    if (!rows.length) {
      return [];
    }

    const groupedByCashier = rows.reduce((accumulator, row) => {
      const cashierName = row?.cashier_name || 'UNKNOWN';
      if (!accumulator[cashierName]) {
        accumulator[cashierName] = {
          cashier: cashierName,
          nettTakings: 0,
          varianceVolume: 0,
          errors: 0,
          offline: 0,
        };
      }

      accumulator[cashierName].nettTakings += toNumber(row.nett_takings);
      accumulator[cashierName].varianceVolume += toNumber(row.variance_volume);
      accumulator[cashierName].errors +=
        toNumber(row.pump_errors_quantity) +
        toNumber(row.shop_void_quantity) +
        toNumber(row.pr_override_quantity);
      accumulator[cashierName].offline += toNumber(row.offline_quantity);

      return accumulator;
    }, {});

    return Object.values(groupedByCashier).sort((a, b) => b.nettTakings - a.nettTakings);
  }, [cashierExceptionTrend]);

  const dailyStatisticsCards = useMemo(() => {
    const summary =
      dailyStatisticsSummary && typeof dailyStatisticsSummary === 'object'
        ? dailyStatisticsSummary
        : {};
    return [
      {
        icon: PeopleOutlineOutlined,
        label: 'Customers Served',
        value: formatValueOrDash(summary.customers_served, (value) => integerFormatter.format(toNumber(value))),
        hint: 'Total unique fuel customers',
        accent: BRAND.cobalt,
      },
      {
        icon: PointOfSaleOutlined,
        label: 'Transactions',
        value: formatValueOrDash(summary.transactions, (value) => integerFormatter.format(toNumber(value))),
        hint: 'Total line items processed',
        accent: BRAND.cobalt,
      },
      {
        icon: AttachMoneyOutlined,
        label: 'Avg Purchase',
        value: formatValueOrDash(summary.avg_purchase, (value) => formatBwp(value)),
        hint: 'Total value / customers',
        accent: BRAND.amber,
      },
      {
        icon: LocalGasStationOutlined,
        label: 'Avg Fill Size',
        value: formatValueOrDash(summary.avg_fill_size, (value) => formatDecimalLitres(value)),
        hint: 'Total volume / line items',
        accent: BRAND.rust,
      },
    ];
  }, [dailyStatisticsSummary]);

  const revenueBarOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '45%',
        },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: {
        categories: revenueByFuelTypeRows.map((item) => formatTenderName(item.fuel_type || item.grade_description)),
        labels: {
          style: { colors: BRAND.textSecondary },
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `BWP ${compactFormatter.format(value)}`,
          style: { colors: BRAND.textSecondary },
        },
      },
      colors: [BRAND.amber, BRAND.rust, BRAND.cobalt, BRAND.teal],
      tooltip: {
        y: {
          formatter: (value) => formatBwp(value),
        },
      },
      noData: {
        text: 'No revenue by fuel type data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [revenueByFuelTypeRows],
  );

  const revenueBarSeries = useMemo(
    () => [
      {
        name: 'Revenue',
        data: revenueByFuelTypeRows.map((item) => toNumber(item.revenue)),
      },
    ],
    [revenueByFuelTypeRows],
  );

  const selectedFuelShareMetric = useMemo(
    () => FUEL_SHARE_METRICS.find((metric) => metric.key === fuelShareMetric) || FUEL_SHARE_METRICS[0],
    [fuelShareMetric],
  );

  const volumeShareRows = useMemo(
    () =>
      Array.isArray(fuelSalesControlVolumeShare?.rows)
        ? fuelSalesControlVolumeShare.rows
        : [],
    [fuelSalesControlVolumeShare],
  );

  const volumeShareOptions = useMemo(
    () => ({
      chart: {
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      labels: volumeShareRows.map((item) => formatTenderName(item.grade_description || item.grade_id)),
      legend: {
        position: 'bottom',
        labels: { colors: [BRAND.textSecondary] },
      },
      stroke: {
        colors: ['#eef4fd'],
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${value.toFixed(1)}%`,
      },
      colors: [BRAND.amber, BRAND.rust, BRAND.cobalt, BRAND.teal],
      tooltip: {
        y: {
          formatter: (value) => `${decimalFormatter.format(value)}%`,
        },
      },
      noData: {
        text: 'No fuel share data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [volumeShareRows],
  );

  const volumeShareSeries = useMemo(
    () => volumeShareRows.map((item) => toNumber(item[selectedFuelShareMetric.key])),
    [volumeShareRows, selectedFuelShareMetric.key],
  );

  const volumeShareTotal = useMemo(
    () => {
      if (!volumeShareRows.length) {
        return null;
      }

      return volumeShareRows.reduce(
        (sum, item) => sum + toNumber(item[selectedFuelShareMetric.totalKey]),
        0,
      );
    },
    [volumeShareRows, selectedFuelShareMetric.totalKey],
  );

  const dailyStatisticsXKey = dailyStatisticsTrend?.x_key || 'batch_date';

  const dailyStatisticsRows = useMemo(() => {
    const rows = Array.isArray(dailyStatisticsTrend?.rows) ? dailyStatisticsTrend.rows : [];
    return [...rows].sort((left, right) => {
      const leftDate = new Date(left?.[dailyStatisticsXKey] || 0).getTime();
      const rightDate = new Date(right?.[dailyStatisticsXKey] || 0).getTime();
      return leftDate - rightDate;
    });
  }, [dailyStatisticsTrend, dailyStatisticsXKey]);

  const selectedDailyStatisticMetric = useMemo(
    () =>
      DAILY_STATISTIC_METRICS.find((metric) => metric.key === dailyStatisticMetric) ||
      DAILY_STATISTIC_METRICS[0],
    [dailyStatisticMetric],
  );

  const dailyStatisticSummary = useMemo(() => {
    const values = dailyStatisticsRows.map((row) => toNumber(row?.[selectedDailyStatisticMetric.key]));
    if (values.length === 0) {
      return { total: null, average: null, peak: null };
    }

    const total = values.reduce((sum, value) => sum + value, 0);
    const peak = Math.max(...values);
    const average = total / values.length;

    return { total, average, peak };
  }, [dailyStatisticsRows, selectedDailyStatisticMetric.key]);

  const trendChartSeries = useMemo(
    () => [
      {
        name: selectedDailyStatisticMetric.label,
        data: dailyStatisticsRows.map((row) => toNumber(row?.[selectedDailyStatisticMetric.key])),
      },
    ],
    [dailyStatisticsRows, selectedDailyStatisticMetric],
  );

  const trendChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.2,
          opacityTo: 0.03,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: dailyStatisticsRows.map((row) => row?.[dailyStatisticsXKey] || ''),
        labels: {
          formatter: (value) => {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
              return value;
            }
            return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          style: { colors: BRAND.textSecondary },
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatDailyStatisticAxis(value, selectedDailyStatisticMetric.format),
          style: { colors: BRAND.textSecondary },
        },
      },
      markers: {
        size: 0,
      },
      colors: [selectedDailyStatisticMetric.color],
      legend: {
        labels: { colors: [BRAND.textSecondary] },
      },
      tooltip: {
        y: {
          formatter: (value) => formatDailyStatisticValue(value, selectedDailyStatisticMetric.format),
        },
      },
      noData: {
        text: 'No daily statistics data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [dailyStatisticsRows, selectedDailyStatisticMetric, dailyStatisticsXKey],
  );

  const selectedFuelSalesControlMetric = useMemo(
    () =>
      FUEL_SALES_CONTROL_METRICS.find((metric) => metric.key === fuelSalesControlMetric) ||
      FUEL_SALES_CONTROL_METRICS[0],
    [fuelSalesControlMetric],
  );

  const fuelSalesControlXKey = fuelSalesControlTrend?.x_key || 'batch_date';

  const fuelSalesControlChart = useMemo(() => {
    const trendSeries = Array.isArray(fuelSalesControlTrend?.series) ? fuelSalesControlTrend.series : [];
    const trendRows = Array.isArray(fuelSalesControlTrend?.rows) ? fuelSalesControlTrend.rows : [];
    const categorySet = new Set();

    if (trendSeries.length > 0) {
      trendSeries.forEach((seriesItem) => {
        (seriesItem?.data || []).forEach((point) => {
          const category = point?.[fuelSalesControlXKey];
          if (category) {
            categorySet.add(category);
          }
        });
      });

      const categories = Array.from(categorySet).sort((left, right) => {
        const leftDate = new Date(left).getTime();
        const rightDate = new Date(right).getTime();
        return leftDate - rightDate;
      });

      const chartSeries = trendSeries.map((seriesItem) => {
        const pointMap = new Map();
        (seriesItem?.data || []).forEach((point) => {
          const category = point?.[fuelSalesControlXKey];
          if (!category) {
            return;
          }
          pointMap.set(category, toNumber(point?.[selectedFuelSalesControlMetric.key]));
        });

        return {
          name: formatTenderName(seriesItem?.name),
          data: categories.map((category) => pointMap.get(category) ?? 0),
        };
      });

      return { categories, series: chartSeries };
    }

    if (trendRows.length > 0) {
      const seriesByFuel = new Map();

      trendRows.forEach((row) => {
        const category = row?.[fuelSalesControlXKey];
        if (!category) {
          return;
        }

        categorySet.add(category);

        const rawFuelLabel =
          row?.grade_description || row?.grade_name || row?.grade_id || row?.fuel_type || 'Fuel';
        const fuelLabel = formatTenderName(rawFuelLabel);
        if (!seriesByFuel.has(fuelLabel)) {
          seriesByFuel.set(fuelLabel, new Map());
        }

        const pointMap = seriesByFuel.get(fuelLabel);
        const metricValue = toNumber(row?.[selectedFuelSalesControlMetric.key]);
        pointMap.set(category, (pointMap.get(category) || 0) + metricValue);
      });

      const categories = Array.from(categorySet).sort((left, right) => {
        const leftDate = new Date(left).getTime();
        const rightDate = new Date(right).getTime();

        if (Number.isNaN(leftDate) || Number.isNaN(rightDate)) {
          return String(left).localeCompare(String(right));
        }

        return leftDate - rightDate;
      });

      const chartSeries = Array.from(seriesByFuel.entries()).map(([fuelLabel, pointMap]) => ({
        name: fuelLabel,
        data: categories.map((category) => pointMap.get(category) ?? 0),
      }));

      return { categories, series: chartSeries };
    }

    return { categories: [], series: [] };
  }, [fuelSalesControlTrend, fuelSalesControlXKey, selectedFuelSalesControlMetric.key]);

  const fuelSalesControlChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      markers: {
        size: 0,
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: fuelSalesControlChart.categories,
        labels: {
          formatter: (value) => {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
              return value;
            }
            return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          style: { colors: BRAND.textSecondary },
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatDailyStatisticAxis(value, selectedFuelSalesControlMetric.format),
          style: { colors: BRAND.textSecondary },
        },
      },
      legend: {
        labels: { colors: [BRAND.textSecondary] },
      },
      colors: [BRAND.amber, BRAND.rust, BRAND.cobalt],
      tooltip: {
        y: {
          formatter: (value) => formatDailyStatisticValue(value, selectedFuelSalesControlMetric.format),
        },
      },
      noData: {
        text: 'No fuel sales control trend data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [fuelSalesControlChart.categories, selectedFuelSalesControlMetric],
  );

  const selectedPumpVarianceMetric = useMemo(
    () =>
      PUMP_VARIANCE_METRICS.find((metric) => metric.key === pumpVarianceMetric) ||
      PUMP_VARIANCE_METRICS[0],
    [pumpVarianceMetric],
  );

  const pumpVarianceChart = useMemo(() => {
    const rows = Array.isArray(pumpVarianceTrend?.rows) ? pumpVarianceTrend.rows : [];
    const xKey = pumpVarianceTrend?.x_key || 'batch_date';
    const categorySet = new Set();
    const pumpValueMap = new Map();
    const pumpMagnitudeMap = new Map();
    const totalByDate = new Map();

    rows.forEach((row) => {
      const category = row?.[xKey];
      if (!category) {
        return;
      }

      const pumpLabel = `Pump ${row?.pump_no || 'N/A'} (${formatTenderName(row?.grade_description || row?.grade_id)})`;
      const metricValue = toNumber(row?.[selectedPumpVarianceMetric.key]);

      categorySet.add(category);

      if (!pumpValueMap.has(pumpLabel)) {
        pumpValueMap.set(pumpLabel, new Map());
      }
      const byDateMap = pumpValueMap.get(pumpLabel);
      byDateMap.set(category, (byDateMap.get(category) || 0) + metricValue);

      pumpMagnitudeMap.set(pumpLabel, (pumpMagnitudeMap.get(pumpLabel) || 0) + Math.abs(metricValue));
      totalByDate.set(category, (totalByDate.get(category) || 0) + metricValue);
    });

    const categories = Array.from(categorySet).sort(
      (left, right) => new Date(left).getTime() - new Date(right).getTime(),
    );

    const topPumpLabels = Array.from(pumpMagnitudeMap.entries())
      .sort((left, right) => right[1] - left[1])
      .slice(0, 6)
      .map(([pumpLabel]) => pumpLabel);

    const series = topPumpLabels.map((pumpLabel) => {
      const byDateMap = pumpValueMap.get(pumpLabel) || new Map();
      return {
        name: pumpLabel,
        data: categories.map((category) => toNumber(byDateMap.get(category))),
      };
    });

    if (categories.length > 0) {
      series.push({
        name: 'Depot Total',
        data: categories.map((category) => toNumber(totalByDate.get(category))),
      });
    }

    const totalCalculated = rows.reduce((sum, row) => sum + toNumber(row?.calculated_sales_volume), 0);
    const totalActual = rows.reduce((sum, row) => sum + toNumber(row?.actual_sales_volume), 0);
    const totalVariance = rows.reduce((sum, row) => sum + toNumber(row?.volume_variance), 0);
    const totalUnsettledVariance = rows.reduce(
      (sum, row) => sum + toNumber(row?.inc_unsettled_vol_variance),
      0,
    );
    const maxAbsoluteVariance = rows.reduce(
      (max, row) => Math.max(max, Math.abs(toNumber(row?.inc_unsettled_vol_variance))),
      0,
    );

    return {
      categories,
      series,
      summary: {
        rows: rows.length,
        totalCalculated,
        totalActual,
        totalVariance,
        totalUnsettledVariance,
        maxAbsoluteVariance,
      },
    };
  }, [pumpVarianceTrend, selectedPumpVarianceMetric.key]);

  const pumpVarianceChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      stroke: {
        curve: 'smooth',
        width: pumpVarianceChart.series.map((seriesItem) =>
          seriesItem.name === 'Depot Total' ? 3.2 : 2.2,
        ),
        dashArray: pumpVarianceChart.series.map((seriesItem) =>
          seriesItem.name === 'Depot Total' ? 0 : 3,
        ),
      },
      markers: {
        size: 0,
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: pumpVarianceChart.categories,
        labels: {
          formatter: (value) => {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
              return value;
            }
            return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          style: { colors: BRAND.textSecondary },
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatDailyStatisticAxis(value, selectedPumpVarianceMetric.format),
          style: { colors: BRAND.textSecondary },
        },
      },
      legend: {
        labels: { colors: [BRAND.textSecondary] },
      },
      colors: ['#60a5fa', '#2563eb', '#38bdf8', '#1d4ed8', '#0ea5e9', '#3b82f6', '#0f172a'],
      tooltip: {
        y: {
          formatter: (value) => formatDailyStatisticValue(value, selectedPumpVarianceMetric.format),
        },
      },
      noData: {
        text: 'No pump variance trend data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [pumpVarianceChart.categories, pumpVarianceChart.series, selectedPumpVarianceMetric.format],
  );

  const cashierBarOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          distributed: true,
        },
      },
      colors: ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#94a3b8', '#64748b'],
      dataLabels: { enabled: false },
      xaxis: {
        categories: cashierRows.map((row) => row.cashier),
        labels: {
          formatter: (value) => `BWP ${compactFormatter.format(value)}`,
          style: { colors: BRAND.textSecondary },
        },
      },
      yaxis: {
        labels: {
          style: { colors: BRAND.textSecondary },
        },
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      tooltip: {
        y: {
          formatter: (value) => formatBwp(value),
        },
      },
      noData: {
        text: 'No cashier performance data for selected filters',
        style: { color: BRAND.textSecondary },
      },
    }),
    [cashierRows],
  );

  const cashierBarSeries = useMemo(
    () => [
      {
        name: 'Nett takings',
        data: cashierRows.map((row) => row.nettTakings),
      },
    ],
    [cashierRows],
  );

  const kpiCards = useMemo(
    () => [
      {
        label: 'Total Revenue',
        value: formatValueOrDash(profitabilityTopSummary?.revenue, (value) => formatCompactBwp(value)),
        hint: 'Month-to-date fuel sales',
        icon: AttachMoneyOutlined,
        accent: BRAND.gold,
      },
      {
        label: 'Total Volume',
        value: formatValueOrDash(profitabilityTopSummary?.volume, (value) => formatDecimalLitres(value)),
        hint: 'Combined volume delivered',
        icon: LocalGasStationOutlined,
        accent: BRAND.amber,
      },
      {
        label: 'Gross Profit',
        value: formatValueOrDash(profitabilityTopSummary?.profit, (value) => formatCompactBwp(value)),
        hint: hasValue(profitabilityTopSummary?.cost)
          ? `Revenue less cost (${formatCompactBwp(profitabilityTopSummary.cost)} cost)`
          : 'Revenue less cost',
        icon: ShoppingCartOutlined,
        accent: BRAND.rust,
      },
      {
        label: 'Gross Margin',
        value: formatPercentOrDash(profitabilityTopSummary?.margin),
        hint: 'Profitability ratio',
        icon: PercentOutlined,
        accent: BRAND.cobalt,
      },
    ],
    [profitabilityTopSummary],
  );

  const cashVarianceCards = useMemo(() => {
    const summary =
      cashVarianceSummary && typeof cashVarianceSummary === 'object'
        ? cashVarianceSummary
        : {};

    return [
      {
        key: 'total_batches',
        label: 'Total Batches',
        value: formatValueOrDash(summary.total_batches, (value) => integerFormatter.format(toNumber(value))),
        tooltip: 'Number of reconciled batches in the selected period.',
        icon: Inventory2Outlined,
        accent: BRAND.teal,
      },
      {
        key: 'total_actual',
        label: 'Total Actual',
        value: formatValueOrDash(summary.total_actual, (value) => formatBwp(value)),
        tooltip: 'Money captured in drawers and cash points.',
        icon: AccountBalanceWalletOutlined,
        accent: BRAND.amber,
      },
      {
        key: 'total_theoretical',
        label: 'Total Theoretical',
        value: formatValueOrDash(summary.total_theoretical, (value) => formatBwp(value)),
        tooltip: 'Expected money based on product movement and sales records.',
        icon: CalculateOutlined,
        accent: BRAND.cobalt,
      },
      {
        key: 'total_variance',
        label: 'Total Variance',
        value: formatValueOrDash(summary.total_variance, (value) => formatBwp(value)),
        tooltip: 'Book-balance value (actual minus theoretical).',
        icon: CompareArrowsOutlined,
        accent: BRAND.success,
      },
      {
        key: 'absolute_variance',
        label: 'Absolute Variance',
        value: formatValueOrDash(summary.absolute_variance, (value) => formatBwp(value)),
        tooltip: 'Machine-detected discrepancy size, ignoring +/- direction.',
        icon: ReportProblemOutlined,
        accent: BRAND.rust,
      },
      {
        key: 'max_positive_variance',
        label: 'Max Positive Variance',
        value: formatValueOrDash(summary.max_positive_variance, (value) => formatBwp(value)),
        tooltip: 'Largest over-reporting case where more money was reported.',
        icon: TrendingUpOutlined,
        accent: BRAND.success,
      },
      {
        key: 'max_negative_variance',
        label: 'Max Negative Variance',
        value: formatValueOrDash(summary.max_negative_variance, (value) => formatBwp(value)),
        tooltip:
          'Largest negative variance (possible shrinkage, theft, cash handling, pump mismatch, or system issue).',
        icon: TrendingDownOutlined,
        accent: BRAND.danger,
      },
    ];
  }, [cashVarianceSummary]);

  const cashierSummaryCards = useMemo(() => {
    const summary =
      cashierExceptionSummary && typeof cashierExceptionSummary === 'object'
        ? cashierExceptionSummary
        : {};
    return [
      {
        key: 'total_cashiers',
        label: 'Total Cashiers',
        value: formatValueOrDash(summary.total_cashiers, (value) => integerFormatter.format(toNumber(value))),
        tooltip: 'Unique cashiers with recorded activity for the selected filters.',
        icon: PeopleOutlineOutlined,
        accent: BRAND.cobalt,
      },
      {
        key: 'total_nett_takings',
        label: 'Total Nett Takings',
        value: formatValueOrDash(summary.total_nett_takings, (value) => formatBwp(value)),
        tooltip: 'Total cashier takings captured in the selected date range.',
        icon: AccountBalanceWalletOutlined,
        accent: BRAND.teal,
      },
      {
        key: 'total_exception_amount',
        label: 'Total Exception Amount',
        value: formatValueOrDash(summary.total_exception_amount, (value) => formatBwp(value)),
        tooltip: 'Monetary impact of cashier exceptions.',
        icon: ReportProblemOutlined,
        accent: BRAND.amber,
      },
      {
        key: 'total_exception_quantity',
        label: 'Total Exception Quantity',
        value: formatValueOrDash(
          summary.total_exception_quantity,
          (value) => integerFormatter.format(toNumber(value)),
        ),
        tooltip: 'Count of exception events (voids, overrides, pump errors, offline events).',
        icon: WarningAmberOutlined,
        accent: BRAND.rust,
      },
    ];
  }, [cashierExceptionSummary]);

  const cashVarianceTrendChart = useMemo(() => {
    const trendSeries = cashVarianceTrend?.series || [];
    const xKey = cashVarianceTrend?.x_key || 'batch_date';
    const categorySet = new Set();

    trendSeries.forEach((series) => {
      (series?.data || []).forEach((point) => {
        if (point?.[xKey]) {
          categorySet.add(point[xKey]);
        }
      });
    });

    const categories = Array.from(categorySet).sort(
      (left, right) => new Date(left).getTime() - new Date(right).getTime(),
    );

    const mappedSeries = trendSeries
      .map((series) => {
        const pointMap = new Map(
          (series?.data || []).map((point) => [point[xKey], toNumber(point[cashVarianceMetric])]),
        );
        return {
          name: formatTenderName(series?.name),
          data: categories.map((category) => pointMap.get(category) ?? 0),
        };
      })
      .filter((series) => series.data.some((value) => Math.abs(value) > 0.0001));

    return {
      categories,
      series: mappedSeries,
    };
  }, [cashVarianceTrend, cashVarianceMetric]);

  const cashVarianceMetricLabel = useMemo(() => {
    const labels = {
      actual: 'Actual',
      theoretical: 'Theoretical',
      variance: 'Variance',
    };
    return labels[cashVarianceMetric] || 'Variance';
  }, [cashVarianceMetric]);

  const cashVarianceTrendOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      stroke: {
        curve: 'smooth',
        width: 2.8,
      },
      markers: {
        size: 3,
      },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: cashVarianceTrendChart.categories,
        labels: {
          formatter: (value) => {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
              return value;
            }
            return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          style: { colors: BRAND.textSecondary },
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatBwp(value),
          style: { colors: BRAND.textSecondary },
        },
      },
      legend: {
        labels: { colors: [BRAND.textSecondary] },
      },
      colors: ['#1d4ed8', '#3b82f6', '#38bdf8', '#60a5fa', '#2563eb', '#64748b'],
      tooltip: {
        y: {
          formatter: (value) => formatBwp(value),
        },
      },
      noData: {
        text: 'No cash variance trend data for the selected filters',
        style: {
          color: BRAND.textSecondary,
        },
      },
    }),
    [cashVarianceTrendChart.categories],
  );

  const batchOptions = useMemo(
    () =>
      [...depotBatches].sort(
        (left, right) => toNumber(right?.batch_number) - toNumber(left?.batch_number),
      ),
    [depotBatches],
  );

  const selectedBatchOption = useMemo(
    () => batchOptions.find((batch) => String(batch.id) === String(selectedBatchId)) || null,
    [batchOptions, selectedBatchId],
  );

  const batchBreakdownRows = useMemo(
    () => (Array.isArray(cashVarianceBatchBreakdown?.data) ? cashVarianceBatchBreakdown.data : []),
    [cashVarianceBatchBreakdown],
  );

  const batchBreakdownMeta = useMemo(() => batchBreakdownRows[0] || null, [batchBreakdownRows]);

  const batchBreakdownSummary = useMemo(
    () => (cashVarianceBatchBreakdown?.summary && typeof cashVarianceBatchBreakdown.summary === 'object'
      ? cashVarianceBatchBreakdown.summary
      : null),
    [cashVarianceBatchBreakdown],
  );

  const batchBreakdownChart = useMemo(() => {
    const categories = batchBreakdownRows.map((row) => formatTenderName(row.tender_type));
    return {
      categories,
      series: [
        {
          name: 'Actual',
          data: batchBreakdownRows.map((row) => toNumber(row.actual)),
        },
        {
          name: 'Theoretical',
          data: batchBreakdownRows.map((row) => toNumber(row.theoretical)),
        },
        {
          name: 'Variance',
          data: batchBreakdownRows.map((row) => toNumber(row.variance)),
        },
      ],
    };
  }, [batchBreakdownRows]);

  const batchBreakdownChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          columnWidth: '48%',
        },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#cfdbeb',
        strokeDashArray: 4,
      },
      xaxis: {
        categories: batchBreakdownChart.categories,
        labels: {
          style: { colors: BRAND.textSecondary },
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => formatBwp(value),
          style: { colors: BRAND.textSecondary },
        },
      },
      legend: {
        labels: { colors: [BRAND.textSecondary] },
      },
      colors: ['#1d4ed8', '#38bdf8', '#64748b'],
      tooltip: {
        y: {
          formatter: (value) => formatBwp(value),
        },
      },
      noData: {
        text: 'No batch breakdown data for this batch',
        style: { color: BRAND.textSecondary },
      },
    }),
    [batchBreakdownChart.categories],
  );

  const heroInputSx = {
    minWidth: { xs: '100%', sm: 180 },
    '& .MuiInputLabel-root': {
      color: 'rgba(238, 246, 255, 0.9)',
    },
    '& .MuiInputBase-input': {
      color: '#f5f9ff',
    },
    '& .MuiSvgIcon-root': {
      color: '#f5f9ff',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(8, 24, 52, 0.46)',
      '& fieldset': {
        borderColor: 'rgba(199, 220, 250, 0.55)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(227, 239, 255, 0.9)',
      },
      '&.Mui-focused fieldset': {
        borderColor: BRAND.gold,
      },
    },
  };

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 1.5, md: 2 },
        pb: 4,
        background: BRAND.pageBackground,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          border: `1px solid ${BRAND.border}`,
          backgroundImage:
            "linear-gradient(105deg, rgba(5, 18, 44, 0.78) 0%, rgba(10, 33, 67, 0.66) 45%, rgba(16, 45, 82, 0.78) 100%), url('/assets/background.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#f3f8ff',
          p: { xs: 2.2, md: 3.5 },
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          spacing={2.5}
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationOnOutlined sx={{ color: BRAND.gold }} />
              <Typography sx={{ color: '#dbe9ff', fontWeight: 700 }}>
                {selectedDepot?.depot_name || selectedDepot?.name || 'Depot Pending Selection'}
              </Typography>
            </Stack>
            <Typography
              sx={{
                mt: 1.2,
                fontSize: { xs: '2.25rem', md: '3.25rem' },
                fontWeight: 700,
                lineHeight: 1.05,
                fontFamily: '"Playfair Display", "Georgia", serif',
              }}
            >
              {reportPeriod}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: { xs: '2.35rem', md: '4rem' },
                lineHeight: 1,
                fontWeight: 700,
                color: '#9ec8ff',
                fontFamily: '"Playfair Display", "Georgia", serif',
              }}
            >
              Performance Report
            </Typography>
            <Typography sx={{ mt: 1.4, color: 'rgba(232, 242, 255, 0.92)', maxWidth: 650 }}>
              End-of-period dashboard summary covering sales, profitability, daily statistics,
              cash variance, and operational health for the selected depot.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.8 }} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                label={`Type: ${formatValueOrDash(selectedDepot?.depot_type, (value) => formatTenderName(value))}`}
                sx={{ backgroundColor: 'rgba(96, 165, 250, 0.3)', color: '#dbeafe' }}
              />
              <Chip
                size="small"
                label={formatValueOrDash(selectedDepot?.email, (value) => value)}
                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.18)', color: '#eff6ff' }}
              />
            </Stack>
          </Box>

          <Stack
            spacing={1.25}
            sx={{
              p: 1.6,
              borderRadius: 2,
              border: '1px solid rgba(199, 220, 250, 0.48)',
              backgroundColor: 'rgba(4, 18, 44, 0.36)',
              minWidth: { xs: '100%', lg: 560 },
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                select
                size="small"
                label="Depot"
                value={selectedDepotId || ''}
                onChange={(event) => onDepotChange(event.target.value)}
                sx={heroInputSx}
                fullWidth
              >
                {depots.map((depot) => (
                  <MenuItem key={`depot-selector-${depot.depot_id}`} value={depot.depot_id}>
                    {depot.depot_name || depot.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(event) => onStartDateChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={heroInputSx}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={endDate}
                onChange={(event) => onEndDateChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: startDate || undefined }}
                sx={heroInputSx}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={onClearDates}
                sx={{
                  minWidth: { xs: '100%', sm: 120 },
                  backgroundColor: BRAND.gold,
                  color: '#08203d',
                  fontWeight: 700,
                  '&:hover': {
                    backgroundColor: '#93c5fd',
                  },
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mt: -3.2, px: { xs: 0.5, md: 0 } }}>
        {kpiCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`kpi-${card.label}`}>
            <MetricTile {...card} />
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          mt: 2.2,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 2.5,
          background: BRAND.cardBackground,
          position: 'sticky',
          top: 8,
          zIndex: 6,
        }}
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 1.5, py: 1.2 }}>
          {DASHBOARD_SECTIONS.map((section) => {
            const isActive = activeSection === section.key;
            return (
              <Button
                key={`section-${section.key}`}
                size="small"
                onClick={() => setActiveSection(section.key)}
                variant={isActive ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  px: 1.5,
                  py: 0.45,
                  minHeight: 34,
                  fontWeight: 700,
                  borderColor: isActive ? BRAND.cobalt : BRAND.border,
                  color: isActive ? '#ffffff' : BRAND.textPrimary,
                  backgroundColor: isActive ? BRAND.cobalt : '#ffffff',
                  boxShadow: isActive ? '0 8px 16px rgba(37, 99, 235, 0.25)' : 'none',
                  '&:hover': {
                    borderColor: BRAND.cobalt,
                    backgroundColor: isActive ? '#1d4ed8' : '#eff6ff',
                  },
                }}
              >
                {section.label}
              </Button>
            );
          })}
        </Stack>
      </Paper>

      {activeSection === 'sales' && (
        <>
      <Box sx={{ mt: 4.5 }}>
        <SectionHeading
          index="01"
          title="Fuel Sales Breakdown"
          description="Revenue, volume, and profitability comparison between ULP 95 and Diesel 50ppm."
        />
        <Grid container spacing={2.2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2.5,
                p: 2,
                height: '100%',
                background: BRAND.cardBackground,
              }}
            >
              <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                Revenue by fuel type
              </Typography>
              <ReactApexChart
                options={revenueBarOptions}
                series={revenueBarSeries}
                type="bar"
                height={320}
              />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2.5,
                p: 2,
                height: '100%',
                background: BRAND.cardBackground,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                  Fuel Share by Grade
                </Typography>
                <TextField
                  select
                  size="small"
                  label="Share Metric"
                  value={fuelShareMetric}
                  onChange={(event) => setFuelShareMetric(event.target.value)}
                  sx={{ minWidth: { xs: '100%', sm: 200 } }}
                >
                  {FUEL_SHARE_METRICS.map((metric) => (
                    <MenuItem key={`fuel-share-metric-${metric.key}`} value={metric.key}>
                      {metric.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, mb: 1 }}>
                {`Total ${selectedFuelShareMetric.label.replace(' %', '')}: ${formatValueOrDash(
                  volumeShareTotal,
                  (value) => formatDailyStatisticValue(value, selectedFuelShareMetric.totalFormat),
                )}`}
              </Typography>
              <ReactApexChart
                options={volumeShareOptions}
                series={volumeShareSeries}
                type="donut"
                height={320}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mt: 2.2,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 2.5,
          p: 2.25,
          background: BRAND.cardBackground,
        }}
      >
        <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1, mb: 2 }}>
          Profitability comparison
        </Typography>
        <Grid container spacing={2}>
          {profitabilityRows.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography sx={{ color: BRAND.textSecondary }}>
                No profitability comparison data for selected filters.
              </Typography>
            </Grid>
          )}
          {profitabilityRows.map((row) => (
            <Grid size={{ xs: 12, md: 6 }} key={row.key}>
              <Box
                sx={{
                  border: `1px solid ${BRAND.border}`,
                  borderLeft: `4px solid ${row.accent}`,
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: '#f8fbff',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography
                    sx={{
                      color: BRAND.textPrimary,
                      fontWeight: 700,
                      fontSize: '1.55rem',
                      fontFamily: '"Playfair Display", "Georgia", serif',
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={formatPercentOrDash(row.margin)}
                    sx={{ backgroundColor: `${row.accent}22`, color: row.accent, fontWeight: 700 }}
                  />
                </Stack>
                <Grid container spacing={1.2} sx={{ mt: 1.3 }}>
                  <Grid size={{ xs: 4 }}>
                    <Typography sx={{ color: BRAND.textSecondary }}>Revenue</Typography>
                    <Typography sx={{ color: BRAND.textPrimary, fontWeight: 700 }}>
                      {formatValueOrDash(row.revenue, (value) => formatCompactBwp(value))}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography sx={{ color: BRAND.textSecondary }}>Cost</Typography>
                    <Typography sx={{ color: BRAND.textPrimary, fontWeight: 700 }}>
                      {formatValueOrDash(row.cost, (value) => formatCompactBwp(value))}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography sx={{ color: BRAND.textSecondary }}>Profit</Typography>
                    <Typography sx={{ color: row.accent, fontWeight: 700 }}>
                      {formatValueOrDash(row.profit, (value) => formatCompactBwp(value))}
                    </Typography>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 1.5,
                    width: '100%',
                    borderRadius: 999,
                    height: 9,
                    backgroundColor: '#e2e8f0',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.max(Math.min(row.progress || row.margin * 3.5, 100), 0)}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${row.accent} 0%, #93c5fd 100%)`,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 2.2,
          border: `1px solid ${BRAND.border}`,
          borderRadius: 2.5,
          p: 2.25,
          background: BRAND.cardBackground,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
              Fuel Sales Control Trend - {reportPeriod}
            </Typography>
            <Typography sx={{ color: BRAND.textSecondary, fontSize: 13 }}>
              Grade comparison from `depots/fuel-sales-control/trend`.
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            label="Metric"
            value={fuelSalesControlMetric}
            onChange={(event) => setFuelSalesControlMetric(event.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 260 } }}
          >
            {FUEL_SALES_CONTROL_METRICS.map((metric) => (
              <MenuItem key={`fuel-sales-control-metric-${metric.key}`} value={metric.key}>
                {metric.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <ReactApexChart
          options={fuelSalesControlChartOptions}
          series={fuelSalesControlChart.series}
          type="line"
          height={330}
        />
      </Paper>
        </>
      )}

      {activeSection === 'trend' && (
      <Box sx={{ mt: 4.5 }}>
        <SectionHeading
          index="02"
          title="Daily Statistics Trend"
          description="Operations trend from batches, including customers, line items, fuel sales value, total fuel volume, and fill behavior."
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {dailyStatisticsCards.map((card) => (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`daily-stat-card-${card.label}`}>
              <InfoTile {...card} />
            </Grid>
          ))}
        </Grid>
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5,
            p: 2,
            background: BRAND.cardBackground,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
              {selectedDailyStatisticMetric.label} - {reportPeriod}
            </Typography>
            <TextField
              select
              size="small"
              label="Metric"
              value={dailyStatisticMetric}
              onChange={(event) => setDailyStatisticMetric(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            >
              {DAILY_STATISTIC_METRICS.map((metric) => (
                <MenuItem key={`daily-stat-metric-${metric.key}`} value={metric.key}>
                  {metric.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label={`Total: ${formatValueOrDash(
                dailyStatisticSummary.total,
                (value) => formatDailyStatisticValue(value, selectedDailyStatisticMetric.format),
              )}`}
              sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
            />
            <Chip
              size="small"
              label={`Average: ${formatValueOrDash(
                dailyStatisticSummary.average,
                (value) => formatDailyStatisticValue(value, selectedDailyStatisticMetric.format),
              )}`}
              sx={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
            />
            <Chip
              size="small"
              label={`Peak: ${formatValueOrDash(
                dailyStatisticSummary.peak,
                (value) => formatDailyStatisticValue(value, selectedDailyStatisticMetric.format),
              )}`}
              sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
            />
          </Stack>
          <ReactApexChart options={trendChartOptions} series={trendChartSeries} type="area" height={360} />
        </Paper>
      </Box>
      )}

      {activeSection === 'reconciliation' && (
      <Box sx={{ mt: 4.5 }}>
        <SectionHeading
          index="03"
          title="Financial Reconciliation"
          description="Cash variance analysis and fuel sales control metrics for operational integrity."
        />
        <Grid container spacing={2} sx={{ mb: 2.2 }}>
          {cashVarianceCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={`cash-card-${card.key}`}>
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 2.5,
                    p: 2,
                    background: BRAND.cardBackground,
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: card.accent,
                    }}
                  />
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.25,
                          backgroundColor: `${card.accent}20`,
                          color: card.accent,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {card.label}
                      </Typography>
                    </Stack>
                    <Tooltip title={card.tooltip} placement="top" arrow>
                      <IconButton size="small" sx={{ color: BRAND.textSecondary }}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography
                    sx={{
                      mt: 1.6,
                      color: BRAND.textPrimary,
                      fontWeight: 700,
                      fontSize: { xs: '1.3rem', sm: '1.45rem' },
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5,
            p: 2,
            mb: 2.2,
            background: BRAND.cardBackground,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.25 }}
          >
            <Box>
              <Typography sx={{ color: BRAND.textPrimary, fontWeight: 700 }}>
                Cash Variance Trend by Tender ({cashVarianceMetricLabel})
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 13 }}>
                Trend from `depots/cash-variance/trend` using selected depot and date filters.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Metric"
              value={cashVarianceMetric}
              onChange={(event) => setCashVarianceMetric(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 180 } }}
            >
              <MenuItem value="variance">Variance</MenuItem>
              <MenuItem value="actual">Actual</MenuItem>
              <MenuItem value="theoretical">Theoretical</MenuItem>
            </TextField>
          </Stack>
          <ReactApexChart
            options={cashVarianceTrendOptions}
            series={cashVarianceTrendChart.series}
            type="line"
            height={320}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5,
            p: 2,
            mb: 2.2,
            background: BRAND.cardBackground,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.25 }}
          >
            <Box>
              <Typography sx={{ color: BRAND.textPrimary, fontWeight: 700 }}>
                Pump Variance Trend (Top Pumps + Depot Total)
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 13 }}>
                Trend from `depots/pump-variance/trend` grouped by pump over batch dates.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Metric"
              value={pumpVarianceMetric}
              onChange={(event) => setPumpVarianceMetric(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            >
              {PUMP_VARIANCE_METRICS.map((metric) => (
                <MenuItem key={`pump-variance-metric-${metric.key}`} value={metric.key}>
                  {metric.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label={`Rows: ${integerFormatter.format(toNumber(pumpVarianceChart.summary.rows))}`}
              sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
            />
            <Chip
              size="small"
              label={`Calculated: ${formatDecimalLitres(pumpVarianceChart.summary.totalCalculated)}`}
              sx={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
            />
            <Chip
              size="small"
              label={`Actual: ${formatDecimalLitres(pumpVarianceChart.summary.totalActual)}`}
              sx={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
            />
            <Chip
              size="small"
              label={`Variance: ${formatDecimalLitres(pumpVarianceChart.summary.totalVariance)}`}
              sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
            />
            <Chip
              size="small"
              label={`Incl. Unsettled: ${formatDecimalLitres(
                pumpVarianceChart.summary.totalUnsettledVariance,
              )}`}
              sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
            />
            <Chip
              size="small"
              label={`Max Anomaly: ${formatDecimalLitres(pumpVarianceChart.summary.maxAbsoluteVariance)}`}
              sx={{ backgroundColor: '#f1f5f9', color: '#334155' }}
            />
          </Stack>

          <ReactApexChart
            options={pumpVarianceChartOptions}
            series={pumpVarianceChart.series}
            type="line"
            height={320}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2.5,
            p: 2,
            mb: 2.2,
            background: BRAND.cardBackground,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography sx={{ color: BRAND.textPrimary, fontWeight: 700 }}>
                Cash Variance Batch Breakdown
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: 13 }}>
                Select a batch to inspect tender-level actual, theoretical, and variance values.
              </Typography>
            </Box>
            <Autocomplete
              size="small"
              options={batchOptions}
              value={selectedBatchOption}
              filterOptions={batchFilterOptions}
              disableClearable={batchOptions.length > 0}
              getOptionLabel={(option) => `Batch #${option.batch_number}`}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              onChange={(_, value) => onBatchChange(value ? String(value.id) : '')}
              noOptionsText={batchOptions.length === 0 ? 'No batches' : 'No matching batch'}
              sx={{ minWidth: { xs: '100%', md: 290 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Batch Search"
                  placeholder="Type batch number"
                  helperText="Search by batch number (list is capped while typing)"
                />
              )}
            />
          </Stack>

          {batchBreakdownMeta && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                label={`Batch #: ${batchBreakdownMeta.batch_number}`}
                sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
              />
              <Chip
                size="small"
                label={`Date: ${batchBreakdownMeta.batch_date || '-'}`}
                sx={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
              />
              <Chip
                size="small"
                label={`Net variance: ${formatValueOrDash(
                  batchBreakdownSummary?.net_variance,
                  (value) => formatBwp(value),
                )}`}
                sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
              />
              <Chip
                size="small"
                label={`Absolute variance: ${formatValueOrDash(
                  batchBreakdownSummary?.absolute_variance,
                  (value) => formatBwp(value),
                )}`}
                sx={{ backgroundColor: '#e2e8f0', color: '#334155' }}
              />
            </Stack>
          )}

          <ReactApexChart
            options={batchBreakdownChartOptions}
            series={batchBreakdownChart.series}
            type="bar"
            height={300}
          />

          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Tender Group</TableCell>
                <TableCell>Tender Type</TableCell>
                <TableCell align="right">Actual</TableCell>
                <TableCell align="right">Theoretical</TableCell>
                <TableCell align="right">Variance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batchBreakdownRows.map((row) => (
                <TableRow key={`batch-breakdown-${row.tender_group}-${row.tender_type}`}>
                  <TableCell>{row.tender_group || '-'}</TableCell>
                  <TableCell>{formatTenderName(row.tender_type)}</TableCell>
                  <TableCell align="right">{formatBwp(row.actual)}</TableCell>
                  <TableCell align="right">{formatBwp(row.theoretical)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: toNumber(row.variance) < 0 ? BRAND.danger : BRAND.success, fontWeight: 700 }}
                  >
                    {formatBwp(row.variance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {SHOW_RECONCILIATION_SNAPSHOT_TABLES && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, xl: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  background: BRAND.cardBackground,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 2, borderBottom: `1px solid ${BRAND.border}` }}
                >
                  <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                    Cash Variance - MTD Summary
                  </Typography>
                  <Chip
                    size="small"
                    label={formatValueOrDash(
                      cashVarianceRows.length
                        ? cashVarianceRows.reduce((sum, row) => sum + (row.actual - row.theoretical), 0)
                        : null,
                      (value) => formatBwp(value),
                    )}
                    sx={{
                      color: BRAND.success,
                      backgroundColor: `${BRAND.success}1f`,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tender Type</TableCell>
                      <TableCell align="right">Actual</TableCell>
                      <TableCell align="right">Theoretical</TableCell>
                      <TableCell align="right">Variance</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashVarianceRows.map((row) => {
                      const variance = row.actual - row.theoretical;
                      const isPositive = variance >= 0;
                      const isWarning = Math.abs(variance) >= 50000;
                      return (
                        <TableRow key={`variance-${row.tender}`}>
                          <TableCell sx={{ fontWeight: row.tender === 'Total' ? 700 : 500 }}>
                            {row.tender}
                          </TableCell>
                          <TableCell align="right">{decimalFormatter.format(row.actual)}</TableCell>
                          <TableCell align="right">{decimalFormatter.format(row.theoretical)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: isPositive ? BRAND.success : BRAND.danger, fontWeight: 700 }}
                          >
                            {`${isPositive ? '+' : ''}${decimalFormatter.format(variance)}`}
                          </TableCell>
                          <TableCell align="center">
                            {isWarning && row.tender !== 'Total' ? (
                              <WarningAmberOutlined sx={{ color: BRAND.amber, fontSize: 18 }} />
                            ) : (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 999,
                                  backgroundColor: isPositive ? BRAND.success : BRAND.danger,
                                  display: 'inline-block',
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {cashVarianceRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No cash variance snapshot data for selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, xl: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  background: BRAND.cardBackground,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 2, borderBottom: `1px solid ${BRAND.border}` }}
                >
                  <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                    Fuel Sales Control - MTD Snapshot
                  </Typography>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Grade</TableCell>
                      <TableCell align="right">MTD Pump Vol</TableCell>
                      <TableCell align="right">MTD Pump Sales</TableCell>
                      <TableCell align="right">MTD Tank Vol</TableCell>
                      <TableCell align="right">Pump Vol Variance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fuelControlRows.map((row) => {
                      const variance = row.pumpVolume - row.tankVolume;
                      return (
                        <TableRow key={`fuel-control-${row.grade}`}>
                          <TableCell sx={{ fontWeight: row.grade === 'Total' ? 700 : 500 }}>{row.grade}</TableCell>
                          <TableCell align="right">{formatLitres(row.pumpVolume)}</TableCell>
                          <TableCell align="right">{formatBwp(row.pumpSales)}</TableCell>
                          <TableCell align="right">{formatLitres(row.tankVolume)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: variance >= 0 ? BRAND.textPrimary : BRAND.danger,
                            }}
                          >
                            {formatLitres(variance)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {fuelControlRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No fuel control snapshot data for selected filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
      )}

      {activeSection === 'cashier' && (
      <Box sx={{ mt: 4.5 }}>
        <SectionHeading
          index="04"
          title="Cashier Performance"
          description="Individual cashier nett takings and exception tracking including volume variance and offline events."
        />
        <Grid container spacing={2} sx={{ mb: 2.2 }}>
          {cashierSummaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid size={{ xs: 12, sm: 6, xl: 3 }} key={`cashier-card-${card.key}`}>
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 2.5,
                    p: 2,
                    background: BRAND.cardBackground,
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: card.accent,
                    }}
                  />
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.25,
                          backgroundColor: `${card.accent}20`,
                          color: card.accent,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon fontSize="small" />
                      </Box>
                      <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {card.label}
                      </Typography>
                    </Stack>
                    <Tooltip title={card.tooltip} placement="top" arrow>
                      <IconButton size="small" sx={{ color: BRAND.textSecondary }}>
                        <InfoOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography
                    sx={{
                      mt: 1.6,
                      color: BRAND.textPrimary,
                      fontWeight: 700,
                      fontSize: { xs: '1.3rem', sm: '1.45rem' },
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, xl: 7 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2.5,
                p: 2,
                background: BRAND.cardBackground,
              }}
            >
              <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                Nett takings by cashier
              </Typography>
              <ReactApexChart options={cashierBarOptions} series={cashierBarSeries} type="bar" height={360} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, xl: 5 }}>
            <Paper
              elevation={0}
              sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2.5,
                overflow: 'hidden',
                background: BRAND.cardBackground,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ p: 2, borderBottom: `1px solid ${BRAND.border}` }}
              >
                <Typography sx={{ color: BRAND.textSecondary, textTransform: 'uppercase', letterSpacing: 1.1 }}>
                  EOS Exceptions
                </Typography>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cashier</TableCell>
                    <TableCell align="right">Var. Vol</TableCell>
                    <TableCell align="right">Errors</TableCell>
                    <TableCell align="right">Offline</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashierRows.map((row) => (
                    <TableRow key={`exception-${row.cashier}`}>
                      <TableCell sx={{ fontWeight: 500 }}>{row.cashier}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: row.varianceVolume < 0 ? BRAND.danger : BRAND.textPrimary }}
                      >
                        {row.varianceVolume === 0 ? '--' : `${row.varianceVolume} L`}
                      </TableCell>
                      <TableCell align="right">{row.errors === 0 ? '--' : row.errors}</TableCell>
                      <TableCell align="right">{row.offline === 0 ? '--' : row.offline}</TableCell>
                    </TableRow>
                  ))}
                  {cashierRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No cashier exception data for selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      )}

    </Box>
  );
}

DepotExecutiveView.propTypes = {
  depots: PropTypes.arrayOf(
    PropTypes.shape({
      depot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      depot_name: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      depot_type: PropTypes.string,
    }),
  ),
  selectedDepotId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDepotChange: PropTypes.func,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onClearDates: PropTypes.func,
  cashVarianceSummary: PropTypes.shape({
    total_batches: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_actual: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_theoretical: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    absolute_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max_positive_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max_negative_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  cashVarianceTrend: PropTypes.shape({
    chart: PropTypes.string,
    x_key: PropTypes.string,
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.object),
      }),
    ),
  }),
  pumpVarianceTrend: PropTypes.shape({
    chart: PropTypes.string,
    x_key: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        batch_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        batch_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        batch_date: PropTypes.string,
        pump_no: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        grade_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        grade_description: PropTypes.string,
        calculated_sales_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        actual_sales_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        volume_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unsettled_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        inc_unsettled_vol_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  profitabilitySummary: PropTypes.shape({
    total_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gross_profit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gross_margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  profitabilityComparison: PropTypes.shape({
    widget: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.string,
        revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        profit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        margin_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        progress_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  fuelSalesControlRevenueByFuelType: PropTypes.shape({
    chart: PropTypes.string,
    chart_type: PropTypes.string,
    x_key: PropTypes.string,
    y_key: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        grade_id: PropTypes.string,
        fuel_type: PropTypes.string,
        revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  fuelSalesControlTrend: PropTypes.shape({
    chart: PropTypes.string,
    x_key: PropTypes.string,
    series: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.object),
      }),
    ),
  }),
  fuelSalesControlVolumeShare: PropTypes.shape({
    chart: PropTypes.string,
    chart_type: PropTypes.string,
    group_key: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        grade_id: PropTypes.string,
        grade_description: PropTypes.string,
        total_sales: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total_variance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sales_share_pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        volume_share_pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        variance_share_pct: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  dailyStatisticsSummary: PropTypes.shape({
    customers_served: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    transactions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_purchase: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    avg_fill_size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  dailyStatisticsTrend: PropTypes.shape({
    chart: PropTypes.string,
    x_key: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        batch_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        batch_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        batch_date: PropTypes.string,
        number_of_fuel_customers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        number_of_line_items: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total_fuel_sales_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        average_fuel_sales_value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        total_fuel_volume: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        average_fill: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  }),
  cashierExceptionSummary: PropTypes.shape({
    total_cashiers: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_nett_takings: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_exception_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_exception_quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  cashierExceptionTrend: PropTypes.shape({
    chart: PropTypes.string,
    x_key: PropTypes.string,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        batch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        batch_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        batch_date: PropTypes.string,
        depot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cashier_name: PropTypes.string,
        nett_takings: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        shop_void_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pr_override_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        variance_volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        pump_errors_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        offline_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  }),
  depotBatches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      depot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      batch_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  selectedBatchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBatchChange: PropTypes.func,
  cashVarianceBatchBreakdown: PropTypes.shape({
    batch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.arrayOf(PropTypes.object),
    summary: PropTypes.shape({
      net_variance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      absolute_variance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
};

DepotExecutiveView.defaultProps = {
  depots: [],
  selectedDepotId: '',
  onDepotChange: () => {},
  startDate: '',
  endDate: '',
  onStartDateChange: () => {},
  onEndDateChange: () => {},
  onClearDates: () => {},
  cashVarianceSummary: null,
  cashVarianceTrend: null,
  pumpVarianceTrend: null,
  profitabilitySummary: null,
  profitabilityComparison: null,
  fuelSalesControlRevenueByFuelType: null,
  fuelSalesControlTrend: null,
  fuelSalesControlVolumeShare: null,
  dailyStatisticsSummary: null,
  dailyStatisticsTrend: null,
  cashierExceptionSummary: null,
  cashierExceptionTrend: null,
  depotBatches: [],
  selectedBatchId: '',
  onBatchChange: () => {},
  cashVarianceBatchBreakdown: null,
};

export default DepotExecutiveView;
